import { CollectionList, Form } from '@mairistem/composants';
import { isNewIdentifiant } from '@mairistem/tools';
import _ from 'lodash';
import React, { type ReactNode } from 'react';
import { toast } from 'react-toastify';

import { DOCUMENT } from '@/constants';
import type { DocumentType, NewlyAddedFileType } from '@/types';
import { downloadDocumentFile, saveBlob } from '@/utils';
import RegiePieceJointeListItem from './RegiePieceJointeListItem';
import RegiePieceJointeListItemForm from './RegiePieceJointeListItemForm';

interface RegiePieceJointeListProps {
  documentsExistant?: DocumentType[];
  addedDocuments?: NewlyAddedFileType[];
  onChange: (e: any) => void;
  onDelete: (document: DocumentType | NewlyAddedFileType) => void;
  control?: React.ReactNode;
  form?: React.ReactNode;
}

const RegiePieceJointeList = (props: RegiePieceJointeListProps) => {
  const {
    documentsExistant = [],
    addedDocuments = [],
    onChange,
    onDelete,
    control = RegiePieceJointeListItem,
    form = RegiePieceJointeListItemForm,
  } = props;

  const handleChange = React.useCallback(
    (e, item) => {
      const errors: Record<string, string> = {};

      if (_.isEmpty(item.titre)) {
        errors.titre = 'Le titre est obligatoire';
      }

      if (_.isNil(item.fichier)) {
        errors.document = 'Le document est obligatoire';
      } else if ((item.fichier?.size ?? 0) / 1024 / 1024 > DOCUMENT.MAX_MO_UPLOAD_SIZE) {
        errors.document = `Le fichier est trop volumineux. Veuillez ne pas dépasser ${DOCUMENT.MAX_MO_UPLOAD_SIZE}mo.`;
      }

      if (_.isEmpty(errors)) {
        onChange({
          ..._.omit(item, 'key'),
        });

        return null;
      }

      return errors;
    },
    [onChange],
  );

  const handleDelete = React.useCallback(
    (e, item) => {
      onDelete(item);
    },
    [onDelete],
  );

  const handleDownload = React.useCallback((e, document) => {
    if (isNewIdentifiant(document.identifiant)) {
      // téléchargement du fichier en local
      // si le document n'a pas encore été enregistré dans la régie
      saveBlob(document.fichier, document.fichier.name);
    } else {
      // téléchargement depuis la bdd
      downloadDocumentFile(
        document.identifiant,
        `${document.nomFichier}.${document.extensionFichier}`,
      ).catch(() => toast.error('Erreur lors du téléchargement de la pièce jointe'));
    }
  }, []);

  // Transforme la liste des documents en liste d'items pour le composant CollectionList
  const items = React.useMemo(() => {
    let result = [];

    if (!_.isEmpty(documentsExistant)) {
      result = [
        ...result,
        ..._.map(documentsExistant, (document) => ({
          key: `${document.identifiant}`,
          ...document,
        })),
      ];
    }

    if (!_.isEmpty(addedDocuments)) {
      result = [
        ...result,
        ..._.map(addedDocuments, (document) => {
          // nom du fichier sans l'extension
          const nomFichier = document.fichier.name;
          // extension du fichier, dernière itération ".XXX" dans le nom
          const extensionFichier = nomFichier.split('.').pop();

          return {
            key: `${document.identifiant}`,
            nomFichier: _.replace(document.fichier.name, `.${extensionFichier}`, ''),
            extensionFichier,
            ...document,
          };
        }),
      ];
    }

    return result;
  }, [documentsExistant, addedDocuments]);

  return (
    <Form.Field
      width={16}
      // @ts-expect-error
      style={{ minHeight: 100 }}
    >
      <CollectionList
        formTitle="Ajout d'un document"
        items={items}
        onItemCreate={handleChange}
        onItemDelete={handleDelete}
        itemControl={control as ReactNode}
        itemForm={form as ReactNode}
        modal={{ size: 'tiny' }}
        horizontal
        addButtonHeight={100}
        addButtonWidth={100}
        style={{ flexWrap: 'wrap' }}
        // @ts-expect-error
        itemStyle={{ height: 100, width: 300 }}
        itemMenu={[
          {
            key: 'download',
            content: 'Télécharger',
            onClick: handleDownload,
            'data-testid': 'regie-pj-download',
          },
        ]}
      />
    </Form.Field>
  );
};

export default RegiePieceJointeList;
